import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  useUserDetailsQuery,
  useUserUpdateMutation,
} from "@ticketingplatform/api/dist/users";
import { Form, message } from "antd";
import { useNumberParams } from "hooks/useNumberParams";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import { useMemo } from "react";
import { ROUTE_PATHS } from "const/paths";
import usePrompt from "hooks/usePrompt";

import { UserFormProps } from "../UserForm/types";

export const useUserEditForm = (): UserFormProps<false> => {
  const [form] = Form.useForm();
  const { id } = useNumberParams(true);
  const intl = useIntl();
  const navigate = useNavigate();
  const { data, loading } = useUserDetailsQuery({ variables: { id } });
  const [updateUser, { error, loading: submitting }] = useUserUpdateMutation();
  const fields = useApolloErrorFields(error);

  usePrompt(submitting);

  return useMemo(
    () => ({
      form,
      fields,
      loading: loading || submitting,
      phoneIsReadOnly: true,
      initialValues: {
        input: {
          firstName: data?.user?.firstName,
          lastName: data?.user?.lastName,
          phoneNumber: data?.user?.phoneNumber || "",
          role: data?.user?.role,
        },
      },
      async onFinish({ input: { firstName, lastName } }) {
        try {
          await updateUser({
            variables: {
              id,
              input: {
                firstName,
                lastName,
              },
            },
          });

          message.success(
            intl.formatMessage(
              {
                defaultMessage: 'User "{name}" has been updated!',
              },
              {
                name: [firstName, lastName].filter(Boolean).join(" "),
              }
            )
          );

          navigate(ROUTE_PATHS.SettingsUsers, {
            state: {
              skipBlock: true,
            },
          });
        } catch {
          message.error(
            intl.formatMessage({
              defaultMessage: "Error. Cannot update User.",
            })
          );
        }
      },
    }),
    [
      data?.user?.firstName,
      data?.user?.lastName,
      data?.user?.phoneNumber,
      data?.user?.role,
      fields,
      form,
      id,
      intl,
      loading,
      navigate,
      submitting,
      updateUser,
    ]
  );
};
